import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { heroProofChips } from '../../config/credibilityConfig';

function HeroPanel({ data, onCTAClick }) {
  const { t, i18n } = useTranslation();

  // Default MIL Compliance Chips (English)
  const defaultMilChips = [
    'MIL-STD-810 alignment (as applicable)',
    'MIL-STD-461 alignment (as applicable)',
    'RAM engineering discipline'
  ];

  // Get translated chips if available
  const milComplianceChips = t('milComplianceChips', { returnObjects: true, defaultValue: defaultMilChips });
  const proofChips = t('proofChips', { returnObjects: true, defaultValue: heroProofChips });

  // Get translated headline parts
  const headlineLine1 = t('hero.headline_line1', { defaultValue: 'Military-Grade' });
  const headlineLine2 = t('hero.headline_line2', { defaultValue: 'Build-to-Specification' });
  const headlineLine3 = t('hero.headline_line3', { defaultValue: 'Engineering & Manufacturing' });
  const subheadline = t('hero.subheadline', { defaultValue: 'Customer requirements translated into validated, acceptance-ready systems.' });
  const supportingText = t('hero.supporting_text', { defaultValue: 'Beyond customer specifications, HADEN applies proven, program-dependent military engineering disciplines—environmental readiness, EMI/EMC integration, Reliability & Maintainability (RAM), and human engineering—so the design survives real-world deployment and closes acceptance cleanly.' });
  
  return (
    <div 
      className="panel hero-gradient relative flex items-center pt-20" 
      style={{ minHeight: '100vh' }}
      data-testid="hero-panel"
    >
      <div className="panel-content">
        <div className="max-w-4xl mx-auto text-center lg:text-left lg:mx-0">
          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold leading-none mb-4"
            style={{ color: '#101957', letterSpacing: '-0.02em' }}
          >
            {headlineLine1}<br />
            {headlineLine2}<br />
            {headlineLine3}
          </motion.h1>
          
          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
            className="text-xl md:text-2xl font-semibold mb-4"
            style={{ color: '#101957' }}
          >
            {subheadline}
          </motion.p>

          {/* MIL Compliance Chips - After Subheadline */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.12 }}
            className="flex flex-wrap gap-2 mb-5 justify-center lg:justify-start"
          >
            {(Array.isArray(milComplianceChips) ? milComplianceChips : defaultMilChips).map((chip, index) => (
              <span 
                key={index} 
                className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium cursor-default select-none"
                style={{ 
                  backgroundColor: 'rgba(16, 25, 87, 0.08)',
                  color: '#101957'
                }}
              >
                {chip}
              </span>
            ))}
          </motion.div>

          {/* Supporting paragraph */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.15 }}
            className="text-base md:text-lg max-w-3xl mb-6"
            style={{ color: '#475569', lineHeight: '1.7' }}
          >
            {supportingText}
          </motion.p>

          {/* Capability Proof Chips */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
            className="flex flex-wrap gap-2 mb-8 justify-center lg:justify-start"
          >
            {(Array.isArray(proofChips) ? proofChips : heroProofChips).map((chip, index) => (
              <span 
                key={index} 
                className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium cursor-default select-none"
                style={{ 
                  backgroundColor: 'rgba(16, 25, 87, 0.08)',
                  color: '#101957'
                }}
              >
                {chip}
              </span>
            ))}
          </motion.div>
          
          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.25 }}
            className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start"
          >
            <button
              onClick={() => onCTAClick('Request Quote')}
              className="btn-primary px-8 py-4 text-base"
              data-testid="hero-request-quote-btn"
            >
              {t('hero.requestQuote')}
            </button>
            <button
              onClick={() => onCTAClick('Technical Discussion')}
              className="btn-secondary px-8 py-4 text-base"
              data-testid="hero-technical-discussion-btn"
            >
              {t('hero.technicalDiscussion')}
            </button>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export default HeroPanel;
